import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BookOpen, Search, Loader2, Plus, X, ShieldCheck, ExternalLink, ShoppingBag, Star,
  Upload, Image as ImageIcon, Trash2, Pencil, Eye, Tag, MinusCircle, PlusCircle, IndianRupee, DollarSign,
  QrCode, Mail, Copy, Truck, CheckCircle2, Info, Zap, ClipboardList,
} from 'lucide-react';
import PoPShell from './PoPShell';
import { usePoPAuth } from '../../context/PoPAuthContext';
import {
  popListBooks, popMyBooks, popGetBook, popCreateBook, popUpdateBook, popDeleteBook,
  popUploadBookCover, popBookCoverUrl, popRegisterBookClick, popCreateBookOrder, roleLabel,
} from '../../lib/popApi';

// SAMANVAI direct-payment metadata
const SAMANVAI_RETAILER = 'SAMANVAI';
const SAMANVAI_EMAIL    = 'karna.vijay@gmail.com';
const SAMANVAI_UPI_ID   = '9963171105@hdfc';
const SAMANVAI_QR_URL   = '/samanvai-upi-qr.jpeg';
const SAMANVAI_SHIP_DAYS = '4-5';
const isSamanvai = (r) => (r || '').trim().toUpperCase() === SAMANVAI_RETAILER;

const FORMATS = [
  { id: 'paperback', label: 'Paperback' },
  { id: 'hardcover', label: 'Hardcover' },
  { id: 'ebook',     label: 'eBook' },
  { id: 'audiobook', label: 'Audiobook' },
];

const CATEGORIES = ['Business', 'Leadership', 'Technology', 'AI & Data', 'Manufacturing', 'Finance', 'Sustainability', 'Case Studies', 'Fiction', 'Biography', 'Other'];
const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];
const SORT_OPTIONS = [
  { id: 'newest',     label: 'Newest' },
  { id: 'popular',    label: 'Most clicked' },
  { id: 'price_asc',  label: 'Price · low' },
  { id: 'price_desc', label: 'Price · high' },
];

const fmtPrice = (amount, currency) => {
  if (amount == null || amount === 0) return 'Free';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(amount);
  } catch (_e) { return `${currency || ''} ${amount}`; }
};

// ---------------------------------------------------------------------------
// Book cover — poster style
// ---------------------------------------------------------------------------
const CoverArt = ({ book, size = 'md' }) => {
  const [imgErr, setImgErr] = useState(false);
  const dims = size === 'lg' ? 'aspect-[2/3] w-full' : 'aspect-[2/3] w-full';
  if (book.cover_url && !imgErr) {
    return (
      <div className={`relative ${dims} rounded-md overflow-hidden bg-slate-800/50 ring-1 ring-white/10`}>
        <img src={popBookCoverUrl(book.id)} alt={book.title} onError={() => setImgErr(true)}
          className="w-full h-full object-cover" />
      </div>
    );
  }
  // Fallback: gradient poster with title
  return (
    <div className={`relative ${dims} rounded-md overflow-hidden bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 ring-1 ring-white/10 flex items-center justify-center p-3 text-center`}>
      <div>
        <BookOpen className="w-6 h-6 text-amber-300/70 mx-auto mb-2" />
        <div className="text-[10px] font-serif font-bold text-white/85 leading-tight line-clamp-3">{book.title}</div>
        <div className="text-[8px] text-white/40 mt-1 uppercase tracking-wider">{book.author_name}</div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Book grid card
// ---------------------------------------------------------------------------
const BookCard = ({ book, onOpen }) => (
  <button onClick={() => onOpen(book.id)} data-testid={`book-card-${book.id}`}
    className="group text-left rounded-xl border border-white/10 bg-slate-900/40 p-3 hover:border-amber-300/40 hover:bg-slate-900/60 transition">
    <CoverArt book={book} />
    <div className="mt-3 space-y-1">
      <div className="text-sm font-serif font-semibold text-white line-clamp-2 leading-tight group-hover:text-amber-200 transition">{book.title}</div>
      <div className="text-[11px] text-white/60 flex items-center gap-1 truncate">
        {book.author_verification_status === 'verified' && <ShieldCheck className="w-3 h-3 text-emerald-400 flex-shrink-0" />}
        <span className="truncate">{book.author_name}</span>
      </div>
      <div className="flex items-center justify-between pt-1">
        <span className="text-amber-300 font-bold text-sm tabular-nums">{fmtPrice(book.price_amount, book.price_currency)}</span>
        {book.format && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/60">{book.format}</span>}
      </div>
    </div>
  </button>
);

// ---------------------------------------------------------------------------
// Notify-SAMANVAI order form (embedded inside the checkout modal)
// ---------------------------------------------------------------------------
const NotifyOrderForm = ({ book, user, onSubmitted, onCancel }) => {
  const [f, setF] = useState({
    buyer_name: user?.name || '',
    buyer_email: user?.email || '',
    buyer_phone: '',
    shipping_address: '',
    city: '', state: '', pin: '', country: 'India',
    txn_ref: '',
    amount_paid: book.price_amount || '',
    currency: book.price_currency || 'INR',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(null);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.buyer_name.trim() || !f.buyer_email.trim() || !f.shipping_address.trim()) {
      setErr('Name, email and shipping address are required'); return;
    }
    setBusy(true); setErr('');
    try {
      const payload = {
        book_id: book.id,
        buyer_name: f.buyer_name.trim(),
        buyer_email: f.buyer_email.trim(),
        buyer_phone: f.buyer_phone.trim(),
        shipping_address: f.shipping_address.trim(),
        city: f.city.trim(),
        state: f.state.trim(),
        pin: f.pin.trim(),
        country: f.country.trim() || 'India',
        txn_ref: f.txn_ref.trim(),
        amount_paid: f.amount_paid === '' ? 0 : parseFloat(f.amount_paid),
        currency: f.currency,
        notes: f.notes.trim(),
      };
      const r = await popCreateBookOrder(payload);
      setOk(r.order);
      onSubmitted && onSubmitted(r.order);
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not submit order');
    } finally { setBusy(false); }
  };

  if (ok) {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-center" data-testid="order-success">
        <CheckCircle2 className="w-8 h-8 text-emerald-300 mx-auto mb-2" />
        <div className="text-lg font-serif font-semibold text-white mb-1">Order noted · we&apos;ll ship in {SAMANVAI_SHIP_DAYS} days.</div>
        <div className="text-[12px] text-white/60 mb-3">
          Reference: <span className="font-mono text-emerald-200">{ok.id.slice(0, 8).toUpperCase()}</span>
          {' '}· we&apos;ll email <span className="text-white/85">{ok.buyer_email}</span> with a tracking update.
        </div>
        <button onClick={onCancel} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-xs text-white/80">Close</button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/60 p-5" data-testid="notify-order-form">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 inline-flex items-center gap-1.5">
          <ClipboardList className="w-3.5 h-3.5" /> NOTIFY SAMANVAI OF YOUR ORDER
        </div>
        <button onClick={onCancel} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={f.buyer_name} onChange={upd('buyer_name')} placeholder="Full name *" data-testid="order-name"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <input type="email" value={f.buyer_email} onChange={upd('buyer_email')} placeholder="Email *" data-testid="order-email"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <input value={f.buyer_phone} onChange={upd('buyer_phone')} placeholder="Phone (WhatsApp preferred)" data-testid="order-phone"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <input value={f.txn_ref} onChange={upd('txn_ref')} placeholder="UPI transaction reference" data-testid="order-txn"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
      </div>
      <textarea rows={2} value={f.shipping_address} onChange={upd('shipping_address')} placeholder="Shipping address (line 1, line 2) *" data-testid="order-address"
        className="mt-2 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
        <input value={f.city} onChange={upd('city')} placeholder="City" data-testid="order-city"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <input value={f.state} onChange={upd('state')} placeholder="State"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <input value={f.pin} onChange={upd('pin')} placeholder="PIN" data-testid="order-pin"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
        <input value={f.country} onChange={upd('country')} placeholder="Country"
          className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
      </div>
      <textarea rows={2} value={f.notes} onChange={upd('notes')} placeholder="Notes (optional) — signed copy? Gift-wrap? Delivery preferences?"
        className="mt-2 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />

      {err && <div className="text-xs text-rose-300 mt-2">{err}</div>}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button onClick={submit} disabled={busy} data-testid="order-submit"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-white text-sm font-semibold shadow-md">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
          Submit order details
        </button>
        <span className="text-[10px] text-white/50">Pay first via UPI, then submit these details. We ship within {SAMANVAI_SHIP_DAYS} days after verifying.</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SAMANVAI direct-payment checkout modal (UPI QR + email confirmation)
// ---------------------------------------------------------------------------
const SamanvaiCheckoutModal = ({ book, onClose }) => {
  const { user } = usePoPAuth();
  const [copied, setCopied] = useState('');
  const [notifyOpen, setNotifyOpen] = useState(false);
  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    } catch (_e) { /* noop */ }
  };
  const mailSubject = encodeURIComponent(`SAMANVAI Book Order · ${book.title}`);
  const mailBody = encodeURIComponent(
    `Hello,\n\nI have completed the UPI payment for the book:\n\n` +
    `• Title: ${book.title}\n` +
    `• Amount: ${book.price_currency || 'INR'} ${book.price_amount}\n` +
    `• Payment date/time: [please fill]\n` +
    `• UPI transaction ID / reference: [please fill]\n\n` +
    `Shipping address:\n` +
    `[Full name]\n[Street]\n[City, State, PIN]\n[Country]\n[Phone]\n\n` +
    `Thank you,`,
  );
  const mailtoUrl = `mailto:${SAMANVAI_EMAIL}?subject=${mailSubject}&body=${mailBody}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="samanvai-checkout-modal">
      <div className="relative max-w-3xl w-full rounded-2xl border border-amber-300/30 bg-slate-950 shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white z-10" data-testid="samanvai-close">
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-amber-300" />
            <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300">SAMANVAI · DIRECT</div>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl text-white leading-tight">Buy directly from the author</h2>
          <p className="text-sm text-white/70 mt-1">Pay via UPI, email the payment confirmation, and we&apos;ll ship your copy in <b className="text-white">{SAMANVAI_SHIP_DAYS} days</b>.</p>

          <div className="grid md:grid-cols-[280px_1fr] gap-6 mt-6">
            {/* QR + amount */}
            <div className="rounded-xl bg-slate-900/60 border border-white/10 p-5 text-center">
              <div className="text-[10px] tracking-wider font-bold uppercase text-white/50 mb-2">Amount</div>
              <div className="text-3xl font-serif font-bold text-amber-300 tabular-nums">
                {fmtPrice(book.price_amount, book.price_currency)}
              </div>
              <div className="mt-4">
                <img
                  src={SAMANVAI_QR_URL}
                  alt="SAMANVAI UPI QR code"
                  className="w-full rounded-lg ring-1 ring-white/10"
                  data-testid="samanvai-qr-image"
                />
              </div>
              <div className="mt-3 text-[10px] tracking-wider uppercase text-white/50">Scan any UPI app</div>
              <button
                onClick={() => copy(SAMANVAI_UPI_ID, 'upi')}
                data-testid="samanvai-copy-upi"
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white/85 font-mono"
              >
                <span className="tracking-wider">{SAMANVAI_UPI_ID}</span>
                {copied === 'upi' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3 h-3 text-white/50" />}
              </button>
            </div>

            {/* Steps */}
            <div>
              <div className="text-[10px] tracking-[0.3em] font-bold text-white/50 mb-3">HOW TO ORDER</div>
              <ol className="space-y-3">
                {[
                  {
                    Icon: QrCode,
                    title: 'Scan & pay',
                    body: <>Open any UPI app (GPay · PhonePe · Paytm · BHIM), scan the QR code, and pay <b className="text-white">{fmtPrice(book.price_amount, book.price_currency)}</b>. UPI ID: <span className="font-mono text-amber-200">{SAMANVAI_UPI_ID}</span></>,
                  },
                  {
                    Icon: Mail,
                    title: 'Email your payment confirmation',
                    body: <>Send an email to <button onClick={() => copy(SAMANVAI_EMAIL, 'email')} data-testid="samanvai-copy-email" className="font-mono text-amber-200 hover:text-amber-100 underline underline-offset-2">{SAMANVAI_EMAIL}</button>{copied === 'email' ? <span className="ml-1 text-emerald-300 text-[10px]">copied ✓</span> : null} with your UPI transaction reference, the book title, and your shipping address.</>,
                  },
                  {
                    Icon: Truck,
                    title: `We ship your book in ${SAMANVAI_SHIP_DAYS} days`,
                    body: <>Once we verify your payment, your copy is dispatched. You&apos;ll receive a tracking update on the same email thread.</>,
                  },
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-md bg-amber-300/15 border border-amber-300/30 text-amber-300 flex items-center justify-center flex-shrink-0">
                      <s.Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white leading-tight">{s.title}</div>
                      <div className="text-[12px] text-white/70 leading-relaxed mt-0.5">{s.body}</div>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-6 flex flex-wrap gap-2">
                <a
                  href={mailtoUrl}
                  data-testid="samanvai-open-email"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold"
                >
                  <Mail className="w-3.5 h-3.5" /> Open pre-filled email
                </a>
                <button
                  onClick={() => setNotifyOpen(true)}
                  data-testid="samanvai-notify-open"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold shadow-md"
                >
                  <ClipboardList className="w-3.5 h-3.5" /> Notify SAMANVAI of my order
                </button>
                <button
                  onClick={() => copy(SAMANVAI_EMAIL, 'email')}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 text-sm font-semibold"
                >
                  <Copy className="w-3.5 h-3.5" /> {copied === 'email' ? 'Copied ✓' : 'Copy email'}
                </button>
              </div>

              {notifyOpen && (
                <div className="mt-5">
                  <NotifyOrderForm
                    book={book}
                    user={user}
                    onSubmitted={() => { /* form transitions to success state internally */ }}
                    onCancel={() => setNotifyOpen(false)}
                  />
                </div>
              )}

              <div className="mt-4 rounded-md bg-white/5 border border-white/10 px-3 py-2.5 flex items-start gap-2">
                <Info className="w-3.5 h-3.5 text-white/50 mt-0.5 flex-shrink-0" />
                <div className="text-[11px] text-white/60 leading-relaxed">
                  Include your <b className="text-white/80">transaction reference</b>, the <b className="text-white/80">book title</b>, and your <b className="text-white/80">shipping address</b> in the email — this speeds up verification and dispatch.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Book detail modal
// ---------------------------------------------------------------------------
const BookDetail = ({ id, onClose }) => {
  const [book, setBook] = useState(null);
  const [busy, setBusy] = useState(true);
  const [samanvaiOpen, setSamanvaiOpen] = useState(false);
  useEffect(() => {
    (async () => {
      setBusy(true);
      try { const r = await popGetBook(id); setBook(r.book); }
      finally { setBusy(false); }
    })();
  }, [id]);

  const onBuy = async (link) => {
    try { await popRegisterBookClick(id); } catch (_e) { /* noop */ }
    if (isSamanvai(link.retailer)) {
      setSamanvaiOpen(true);
      return;
    }
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="book-detail-modal">
      <div className="relative max-w-4xl w-full rounded-2xl border border-white/10 bg-slate-950 shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white z-10" data-testid="book-detail-close">
          <X className="w-5 h-5" />
        </button>

        {busy || !book ? (
          <div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="grid md:grid-cols-[280px_1fr] gap-6 p-6 sm:p-8">
            {/* Cover */}
            <div className="w-full">
              <CoverArt book={book} size="lg" />
              <div className="mt-3 flex items-center justify-between text-[10px] text-white/40">
                <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {book.views || 0} views</span>
                <span className="inline-flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> {book.clicks || 0} clicks</span>
              </div>
            </div>

            {/* Details */}
            <div>
              {book.category && <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">{book.category.toUpperCase()}</div>}
              <h2 className="font-serif text-2xl sm:text-3xl text-white leading-tight">{book.title}</h2>
              {book.subtitle && <div className="text-sm text-white/70 mt-1">{book.subtitle}</div>}

              <div className="flex items-center gap-2 mt-3">
                {book.author_verification_status === 'verified' && <ShieldCheck className="w-4 h-4 text-emerald-400" />}
                <div className="text-sm text-white/85 font-medium">{book.author_name}</div>
                <div className="text-[10px] text-white/40">· {roleLabel(book.author_role)}</div>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-white/10 text-[11px] text-white/60">
                {book.format && <span className="capitalize"><b className="text-white/85">{book.format}</b></span>}
                {book.pages ? <span>{book.pages} pages</span> : null}
                {book.language && <span>{book.language}</span>}
                {book.publisher && <span>{book.publisher}</span>}
                {book.published_year ? <span>{book.published_year}</span> : null}
                {book.isbn && <span className="tabular-nums">ISBN {book.isbn}</span>}
              </div>

              <div className="mt-5">
                <div className="text-[10px] tracking-[0.3em] font-bold text-white/50 mb-2">SYNOPSIS</div>
                <p className="text-sm text-white/85 leading-relaxed whitespace-pre-line">{book.synopsis}</p>
              </div>

              {book.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {book.tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">
                      <Tag className="w-2.5 h-2.5" /> {t}
                    </span>
                  ))}
                </div>
              )}

              {/* Buy links */}
              <div className="mt-6 pt-5 border-t border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300">GET YOUR COPY</div>
                  <div className="text-amber-300 font-bold text-2xl tabular-nums">{fmtPrice(book.price_amount, book.price_currency)}</div>
                </div>
                {book.order_links && book.order_links.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-2">
                    {book.order_links.map((l, i) => (
                      <button key={`${l.retailer}-${i}`} onClick={() => onBuy(l)} data-testid={`book-buy-${i}`}
                        className={`w-full inline-flex items-center justify-between gap-2 px-4 py-2.5 rounded-md text-sm font-semibold shadow-md transition ${
                          isSamanvai(l.retailer)
                            ? 'bg-emerald-500 hover:bg-emerald-400 text-white ring-1 ring-emerald-300/50'
                            : 'bg-amber-300 hover:bg-amber-200 text-slate-900'
                        }`}>
                        <span className="inline-flex items-center gap-1.5">
                          {isSamanvai(l.retailer) ? <><Zap className="w-3.5 h-3.5" /> Buy direct on SAMANVAI</> : <><ShoppingBag className="w-3.5 h-3.5" /> Buy on {l.retailer}</>}
                        </span>
                        {isSamanvai(l.retailer) ? <QrCode className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-white/50 italic">The author hasn&apos;t added any purchase links yet.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {samanvaiOpen && book && (
        <SamanvaiCheckoutModal book={book} onClose={() => setSamanvaiOpen(false)} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Book editor modal (create + edit)
// ---------------------------------------------------------------------------
const emptyBook = {
  title: '', subtitle: '', synopsis: '', category: 'Business',
  price_amount: '', price_currency: 'INR',
  order_links: [{ retailer: 'Amazon', url: '' }],
  tags: [], isbn: '', publisher: '', language: 'English',
  format: 'paperback', pages: '', published_year: '',
};

const BookEditor = ({ book, onClose, onSaved }) => {
  const isEdit = !!book;
  const [f, setF] = useState(book ? {
    ...emptyBook, ...book,
    tags: book.tags || [],
    order_links: (book.order_links && book.order_links.length > 0) ? book.order_links : [{ retailer: 'Amazon', url: '' }],
    price_amount: book.price_amount ?? '',
    pages: book.pages ?? '',
    published_year: book.published_year ?? '',
  } : emptyBook);
  const [tagDraft, setTagDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(book?.cover_url ? popBookCoverUrl(book.id) : '');
  const [uploadPct, setUploadPct] = useState(0);
  const fileRef = useRef(null);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t || f.tags.includes(t)) return;
    setF({ ...f, tags: [...f.tags, t] });
    setTagDraft('');
  };
  const removeTag = (i) => setF({ ...f, tags: f.tags.filter((_, j) => j !== i) });

  const setLink = (i, k, val) => {
    const links = [...f.order_links];
    links[i] = { ...links[i], [k]: val };
    setF({ ...f, order_links: links });
  };
  const addLink = () => setF({ ...f, order_links: [...f.order_links, { retailer: '', url: '' }] });
  const removeLink = (i) => setF({ ...f, order_links: f.order_links.filter((_, j) => j !== i) });

  const pickCover = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr('Cover image must be ≤ 5 MB'); return; }
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!['.jpg', '.jpeg', '.png', '.webp'].includes(ext)) { setErr('Only JPG, PNG, WEBP images are allowed'); return; }
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    setErr('');
  };

  const submit = async () => {
    if (!f.title.trim() || !f.synopsis.trim()) { setErr('Title and synopsis are required'); return; }
    setBusy(true); setErr('');
    try {
      const payload = {
        title: f.title.trim(),
        subtitle: f.subtitle.trim(),
        synopsis: f.synopsis.trim(),
        category: f.category,
        price_amount: f.price_amount === '' ? 0 : parseFloat(f.price_amount),
        price_currency: f.price_currency,
        order_links: f.order_links.filter((l) => l.retailer.trim() && (isSamanvai(l.retailer) || l.url.trim())),
        tags: f.tags,
        isbn: f.isbn.trim(),
        publisher: f.publisher.trim(),
        language: f.language.trim() || 'English',
        format: f.format,
        pages: f.pages === '' ? 0 : parseInt(f.pages, 10),
        published_year: f.published_year === '' ? null : parseInt(f.published_year, 10),
      };
      let saved;
      if (isEdit) {
        saved = (await popUpdateBook(book.id, payload)).book;
      } else {
        saved = (await popCreateBook(payload)).book;
      }
      // Upload cover if selected
      if (coverFile) {
        await popUploadBookCover(saved.id, coverFile, (ev) => {
          if (ev.total) setUploadPct(Math.round((ev.loaded * 100) / ev.total));
        });
      }
      onSaved(saved);
    } catch (e) {
      setErr((e && e.response && e.response.data && e.response.data.detail) || 'Could not save book');
    } finally { setBusy(false); setUploadPct(0); }
  };

  const currencyIcon = f.price_currency === 'INR' ? IndianRupee : DollarSign;
  const CI = currencyIcon;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="book-editor-modal">
      <div className="relative max-w-3xl w-full rounded-2xl border border-white/10 bg-slate-950 shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white z-10">
          <X className="w-5 h-5" />
        </button>
        <div className="p-6 sm:p-8">
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-1">{isEdit ? 'EDIT LISTING' : 'LIST A NEW BOOK'}</div>
          <h2 className="font-serif text-2xl text-white mb-6">{isEdit ? 'Update your book' : 'Advertise & sell your book'}</h2>

          <div className="grid md:grid-cols-[200px_1fr] gap-6">
            {/* Cover uploader */}
            <div>
              <label className="block text-[10px] tracking-wider font-bold uppercase text-white/70 mb-1.5">Cover image</label>
              <div className="aspect-[2/3] rounded-md overflow-hidden border-2 border-dashed border-white/15 bg-slate-900/60 flex items-center justify-center relative">
                {coverPreview ? (
                  <img src={coverPreview} alt="cover" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center p-3">
                    <ImageIcon className="w-6 h-6 text-white/30 mx-auto mb-1.5" />
                    <div className="text-[10px] text-white/50">No cover yet</div>
                  </div>
                )}
                {uploadPct > 0 && uploadPct < 100 && (
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-white/10">
                    <div className="h-full bg-amber-300" style={{ width: `${uploadPct}%` }} />
                  </div>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => pickCover(e.target.files?.[0])}
                data-testid="book-cover-input" className="hidden" />
              <button onClick={() => fileRef.current?.click()} type="button" data-testid="book-cover-pick"
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 text-xs font-semibold">
                <Upload className="w-3.5 h-3.5" /> {coverPreview ? 'Replace' : 'Upload'} cover
              </button>
              <div className="text-[9px] text-white/40 mt-1 text-center">JPG · PNG · WEBP · ≤ 5MB</div>
            </div>

            {/* Fields */}
            <div className="space-y-3">
              <input value={f.title} onChange={upd('title')} placeholder="Title *" data-testid="book-form-title"
                className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
              <input value={f.subtitle} onChange={upd('subtitle')} placeholder="Subtitle (optional)" data-testid="book-form-subtitle"
                className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
              <textarea value={f.synopsis} onChange={upd('synopsis')} rows={4} placeholder="Synopsis * — what will readers gain from this book?" data-testid="book-form-synopsis"
                className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />

              <div className="grid sm:grid-cols-2 gap-2">
                <select value={f.category} onChange={upd('category')} data-testid="book-form-category"
                  className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
                  {CATEGORIES.map((c) => <option key={c} className="bg-slate-900">{c}</option>)}
                </select>
                <select value={f.format} onChange={upd('format')} data-testid="book-form-format"
                  className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
                  {FORMATS.map((c) => <option key={c.id} value={c.id} className="bg-slate-900">{c.label}</option>)}
                </select>
              </div>

              <div className="grid sm:grid-cols-[100px_1fr] gap-2">
                <select value={f.price_currency} onChange={upd('price_currency')} data-testid="book-form-currency"
                  className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
                  {CURRENCIES.map((c) => <option key={c} className="bg-slate-900">{c}</option>)}
                </select>
                <div className="relative">
                  <CI className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="number" min="0" step="1" value={f.price_amount} onChange={upd('price_amount')}
                    data-testid="book-form-price" placeholder="Price (0 for free)"
                    className="w-full pl-9 pr-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-2">
                <input value={f.publisher} onChange={upd('publisher')} placeholder="Publisher"
                  className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
                <input value={f.language} onChange={upd('language')} placeholder="Language"
                  className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
                <input type="number" min="1900" max="2100" value={f.published_year} onChange={upd('published_year')} placeholder="Year"
                  className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <input value={f.isbn} onChange={upd('isbn')} placeholder="ISBN (optional)"
                  className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
                <input type="number" min="0" value={f.pages} onChange={upd('pages')} placeholder="Pages"
                  className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
              </div>

              {/* Order links */}
              <div>
                <label className="block text-[10px] tracking-wider font-bold uppercase text-white/70 mb-1.5">Where to buy</label>
                <div className="space-y-2">
                  {f.order_links.map((l, i) => {
                    const samanvai = isSamanvai(l.retailer);
                    return (
                      <div key={i} className="grid grid-cols-[140px_1fr_36px] gap-2">
                        <input value={l.retailer} onChange={(e) => setLink(i, 'retailer', e.target.value)}
                          placeholder="Amazon, Flipkart, SAMANVAI…" data-testid={`book-form-retailer-${i}`}
                          className={`px-3 py-2 rounded-md border text-sm text-white placeholder:text-white/30 ${samanvai ? 'bg-emerald-500/10 border-emerald-400/40' : 'bg-white/5 border-white/10'}`} />
                        {samanvai ? (
                          <div data-testid={`book-form-samanvai-badge-${i}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-400/40 text-emerald-200 text-[11px]">
                            <Zap className="w-3.5 h-3.5" /> Direct-payment · buyers pay via UPI QR and email confirmation — no URL needed
                          </div>
                        ) : (
                          <input type="url" value={l.url} onChange={(e) => setLink(i, 'url', e.target.value)}
                            placeholder="https://…" data-testid={`book-form-url-${i}`}
                            className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
                        )}
                        <button type="button" onClick={() => removeLink(i)}
                          className="px-2 rounded-md bg-white/5 hover:bg-rose-500/10 border border-white/10 text-white/50 hover:text-rose-300">
                          <MinusCircle className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                  <div className="flex flex-wrap gap-3">
                    <button type="button" onClick={addLink} data-testid="book-form-add-link"
                      className="inline-flex items-center gap-1 text-xs text-amber-300 hover:text-amber-200 font-semibold">
                      <PlusCircle className="w-3.5 h-3.5" /> Add another retailer
                    </button>
                    {!f.order_links.some((l) => isSamanvai(l.retailer)) && (
                      <button type="button"
                        onClick={() => setF({ ...f, order_links: [...f.order_links, { retailer: SAMANVAI_RETAILER, url: '' }] })}
                        data-testid="book-form-add-samanvai"
                        className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200 font-semibold">
                        <Zap className="w-3.5 h-3.5" /> Enable SAMANVAI direct-buy
                      </button>
                    )}
                  </div>
                  <div className="mt-2 rounded-md bg-white/5 border border-white/10 px-3 py-2 text-[11px] text-white/60 leading-relaxed">
                    <div className="inline-flex items-center gap-1 font-semibold text-white/80 mb-0.5"><Info className="w-3 h-3" /> Not on Amazon or Flipkart?</div>
                    Add a retailer named <b className="text-emerald-300">SAMANVAI</b> and leave the URL empty — buyers will see a UPI QR + email flow to pay you directly, and we&apos;ll ship in {SAMANVAI_SHIP_DAYS} days.
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-[10px] tracking-wider font-bold uppercase text-white/70 mb-1.5">Tags</label>
                <div className="flex gap-2">
                  <input value={tagDraft} onChange={(e) => setTagDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                    placeholder="e.g. AI, Leadership, Case Study"
                    className="flex-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30" />
                  <button type="button" onClick={addTag}
                    className="px-3 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/80"><Plus className="w-4 h-4" /></button>
                </div>
                {f.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {f.tags.map((t, i) => (
                      <span key={`${t}-${i}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200">
                        {t}
                        <button onClick={() => removeTag(i)} type="button" className="hover:text-white"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {err && <div className="text-xs text-rose-300">{err}</div>}
              <div className="flex gap-2 pt-2">
                <button onClick={submit} disabled={busy || !f.title.trim() || !f.synopsis.trim()} data-testid="book-form-submit"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-amber-300 hover:bg-amber-200 disabled:opacity-40 text-slate-900 text-sm font-semibold shadow-md">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {isEdit ? 'Save changes' : 'Publish book'}
                </button>
                <button onClick={onClose} className="px-5 py-2.5 rounded-md bg-white/5 hover:bg-white/10 text-white/70 text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Author's row card (in "My Books" tab)
// ---------------------------------------------------------------------------
const AuthorRow = ({ b, onEdit, onDelete, onOpen }) => (
  <div className="grid grid-cols-[64px_1fr_auto] gap-3 items-center rounded-lg border border-white/10 bg-slate-900/40 p-3">
    <div className="w-16 aspect-[2/3] rounded overflow-hidden bg-slate-800/50 ring-1 ring-white/10">
      {b.cover_url ? <img src={popBookCoverUrl(b.id)} alt="" className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full"><BookOpen className="w-4 h-4 text-white/30" /></div>}
    </div>
    <div className="min-w-0">
      <div className="text-sm font-serif font-semibold text-white truncate">{b.title}</div>
      <div className="text-[11px] text-white/60 truncate">{b.category || 'Uncategorised'} · {b.format || 'paperback'} · {fmtPrice(b.price_amount, b.price_currency)}</div>
      <div className="flex items-center gap-3 mt-1 text-[10px] text-white/50">
        <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {b.views || 0}</span>
        <span className="inline-flex items-center gap-1"><ShoppingBag className="w-3 h-3" /> {b.clicks || 0} clicks</span>
      </div>
    </div>
    <div className="flex items-center gap-1.5">
      <button onClick={() => onOpen(b.id)} title="Preview" data-testid={`mybook-preview-${b.id}`}
        className="p-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/70"><Eye className="w-3.5 h-3.5" /></button>
      <button onClick={() => onEdit(b)} title="Edit" data-testid={`mybook-edit-${b.id}`}
        className="p-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/70"><Pencil className="w-3.5 h-3.5" /></button>
      <button onClick={() => onDelete(b.id)} title="Delete" data-testid={`mybook-delete-${b.id}`}
        className="p-2 rounded-md bg-white/5 hover:bg-rose-500/10 border border-rose-400/30 text-rose-300"><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const PoPBooks = () => {
  const { user } = usePoPAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('browse');
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [format, setFormat] = useState('');
  const [sort, setSort] = useState('newest');

  const [items, setItems] = useState([]);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [editorBook, setEditorBook] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  // Deep-link support: /pop/books?book=<id> auto-opens the detail modal.
  useEffect(() => {
    const b = searchParams.get('book');
    if (b) {
      setOpenId(b);
      const next = new URLSearchParams(searchParams);
      next.delete('book');
      setSearchParams(next, { replace: true });
    }
  }, []);

  const canPublish = user && ['industry_expert', 'faculty', 'super_admin'].includes(user.role);

  const load = async () => {
    setBusy(true);
    try {
      if (tab === 'browse') {
        const params = { sort };
        if (q) params.q = q;
        if (category) params.category = category;
        if (format) params.format = format;
        const r = await popListBooks(params);
        setItems(r.results || []);
      } else if (tab === 'mine' && user) {
        const r = await popMyBooks();
        setMine(r.results || []);
      }
    } catch (_e) { /* noop */ }
    finally { setBusy(false); }
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [tab, q, category, format, sort]);

  const openEditor = (b) => { setEditorBook(b || null); setEditorOpen(true); };
  const onSaved = () => { setEditorOpen(false); setEditorBook(null); load(); };
  const doDelete = async (id) => {
    if (!window.confirm('Delete this book listing? This cannot be undone.')) return;
    await popDeleteBook(id); load();
  };

  return (
    <PoPShell activeTab="books">
      {/* Hero */}
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-2">BOOKS MARKETPLACE</div>
          <h1 className="font-serif text-3xl sm:text-4xl">The bookstore of the practitioner network.</h1>
          <p className="text-sm text-white/60 mt-1 max-w-2xl">Books authored by verified Professors of Practice — case studies, leadership, industry playbooks, and more. Read the synopsis, then buy from your preferred retailer.</p>
        </div>
        {canPublish && (
          <button onClick={() => openEditor(null)} data-testid="book-new"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold shadow-md">
            <Plus className="w-4 h-4" /> List a book
          </button>
        )}
      </div>

      {/* Tabs */}
      {canPublish && (
        <div className="flex gap-1.5 mb-5">
          <button onClick={() => setTab('browse')} data-testid="book-tab-browse"
            className={`px-3 py-1.5 rounded text-xs font-semibold border transition ${tab === 'browse' ? 'bg-amber-300 text-slate-900 border-amber-300' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'}`}>
            Browse
          </button>
          <button onClick={() => setTab('mine')} data-testid="book-tab-mine"
            className={`px-3 py-1.5 rounded text-xs font-semibold border transition ${tab === 'mine' ? 'bg-amber-300 text-slate-900 border-amber-300' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'}`}>
            My books
          </button>
        </div>
      )}

      {tab === 'browse' && (
        <>
          {/* Filters */}
          <div className="grid grid-cols-2 md:grid-cols-[1fr_180px_180px_180px] gap-2 mb-6">
            <div className="relative col-span-2 md:col-span-1">
              <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="book-search"
                placeholder="Search titles, authors, tags…"
                className="w-full pl-9 pr-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-300" />
            </div>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="book-filter-category"
              className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
              <option value="" className="bg-slate-900">All categories</option>
              {CATEGORIES.map((c) => <option key={c} className="bg-slate-900">{c}</option>)}
            </select>
            <select value={format} onChange={(e) => setFormat(e.target.value)} data-testid="book-filter-format"
              className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
              <option value="" className="bg-slate-900">All formats</option>
              {FORMATS.map((c) => <option key={c.id} value={c.id} className="bg-slate-900">{c.label}</option>)}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="book-sort"
              className="px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white">
              {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id} className="bg-slate-900">{o.label}</option>)}
            </select>
          </div>

          {busy ? (
            <div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading books…</div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-16 text-center">
              <BookOpen className="w-10 h-10 text-white/25 mx-auto mb-3" />
              <div className="text-base text-white/70 mb-1">No books to show yet.</div>
              <div className="text-xs text-white/40">{canPublish ? 'Be the first to list a book.' : 'Check back soon — practitioners are curating their work.'}</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {items.map((b) => <BookCard key={b.id} book={b} onOpen={setOpenId} />)}
            </div>
          )}
        </>
      )}

      {tab === 'mine' && canPublish && (
        <>
          {busy ? (
            <div className="flex items-center justify-center py-16 text-white/60"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
          ) : mine.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 bg-slate-900/40 p-12 text-center">
              <BookOpen className="w-8 h-8 text-white/25 mx-auto mb-2" />
              <div className="text-sm text-white/70 mb-3">You haven&apos;t listed any books yet.</div>
              <button onClick={() => openEditor(null)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-sm font-semibold">
                <Plus className="w-4 h-4" /> List your first book
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {mine.map((b) => <AuthorRow key={b.id} b={b} onEdit={openEditor} onDelete={doDelete} onOpen={setOpenId} />)}
            </div>
          )}
        </>
      )}

      {openId && <BookDetail id={openId} onClose={() => setOpenId(null)} />}
      {editorOpen && <BookEditor book={editorBook} onClose={() => { setEditorOpen(false); setEditorBook(null); }} onSaved={onSaved} />}
    </PoPShell>
  );
};

export default PoPBooks;
